import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import CookieBanner from './components/CookieBanner';
import LaunchPage from './pages/LaunchPage';

// Page titles
const PAGE_TITLES = {
  '/': 'SOPREL Groupe | Fabricant béton précontraint depuis 1957',
  '/groupe': 'Le Groupe | SOPREL Groupe',
  '/produits': 'Nos Produits | SOPREL Groupe',
  '/engagements': 'Nos Engagements | SOPREL Groupe',
  '/realisations': 'Réalisations | SOPREL Groupe',
  '/recrutement': 'Recrutement | SOPREL Groupe',
  '/contact': 'Contact | SOPREL Groupe',
  '/mentions-legales': 'Mentions Légales | SOPREL Groupe',
  '/politique-confidentialite': 'Politique de Confidentialité | SOPREL Groupe',
  '/admin/login': 'Administration | SOPREL Groupe',
};

// Scroll to top + set title on every route change
const RouteManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    const title = PAGE_TITLES[pathname]
      || (pathname.startsWith('/produits/') ? 'Produit | SOPREL Groupe' : null)
      || (pathname.startsWith('/admin') ? 'Administration | SOPREL Groupe' : null)
      || 'SOPREL Groupe';
    document.title = title;
  }, [pathname]);
  return null;
};

// Public Pages
import HomePage from './pages/HomePage';
import GroupePage from './pages/GroupePage';
import ProduitsPage from './pages/ProduitsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import EngagementsPage from './pages/EngagementsPage';
import RealisationsPage from './pages/RealisationsPage';
import RecrutementPage from './pages/RecrutementPage';
import ContactPage from './pages/ContactPage';
import MentionsLegalesPage from './pages/MentionsLegalesPage';
import PolitiqueConfidentialitePage from './pages/PolitiqueConfidentialitePage';

// Admin Pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminPageContent from './pages/admin/AdminPageContent';
import AdminContent from './pages/admin/AdminContent';
import AdminProducts from './pages/admin/AdminProducts';
import AdminRealisations from './pages/admin/AdminRealisations';
import AdminJobs from './pages/admin/AdminJobs';
import AdminMessages from './pages/admin/AdminMessages';

// Layout
import PublicLayout from './components/layout/PublicLayout';
import AdminLayout from './components/layout/AdminLayout';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/groupe" element={<GroupePage />} />
        <Route path="/produits" element={<ProduitsPage />} />
        <Route path="/produits/:slug" element={<ProductDetailPage />} />
        <Route path="/engagements" element={<EngagementsPage />} />
        <Route path="/realisations" element={<RealisationsPage />} />
        <Route path="/recrutement" element={<RecrutementPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
        <Route path="/politique-confidentialite" element={<PolitiqueConfidentialitePage />} />
      </Route>

      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="pages" element={<AdminPageContent />} />
        <Route path="content" element={<AdminContent />} />
        <Route path="products" element={<AdminProducts />} />
        <Route path="realisations" element={<AdminRealisations />} />
        <Route path="jobs" element={<AdminJobs />} />
        <Route path="messages" element={<AdminMessages />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <RouteManager />
        <AppRoutes />
        <CookieBanner />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
